import { ChangeEvent, useState } from "react";
import { Box, Grid2 as Grid, Typography } from "@mui/material";
import moment, { Moment } from "moment";
import { FieldError } from "react-hook-form";
import { v4 as uuidv4 } from "uuid";
import { useAtom } from "jotai";
import { Button, DatePicker, TextField } from "../../../common";
import { selectedOrgIdAtom } from "../../../../atoms";
import { useFetchLeavesQuery } from "../../../../api/userService";
import { CrewLeave } from "../../../../types";
import { formatDate, theme } from "../../../../utils";

const DISPLAY_DATE_FORMAT = "MMM DD, YYYY";

type LeaveField = "reason" | "startDate" | "endDate";

const toFieldError = (message?: string) =>
  message ? ({ type: "manual", message } as FieldError) : undefined;

type CrewLeavesProps = {
  userId: number;
  // Leave added in this session; saved on Save Changes.
  leaves: CrewLeave[];
  onChange: (leaves: CrewLeave[]) => void;
};

export const CrewLeaves = ({ userId, leaves, onChange }: CrewLeavesProps) => {
  const [selectedOrgId] = useAtom(selectedOrgIdAtom);
  const [reason, setReason] = useState("");
  const [startDate, setStartDate] = useState<Moment | null>(null);
  const [endDate, setEndDate] = useState<Moment | null>(null);
  const [errors, setErrors] = useState<Partial<Record<LeaveField, string>>>({});
  const fetchLeavesQuery = useFetchLeavesQuery(selectedOrgId, userId, {
    enabled: selectedOrgId && userId ? true : false,
  });

  const sortedLeaves = [...leaves, ...(fetchLeavesQuery.data ?? [])].sort(
    (a, b) => b.startDate.localeCompare(a.startDate),
  );

  const handleAddLeave = () => {
    const newErrors: Partial<Record<LeaveField, string>> = {};

    if (!reason.trim()) newErrors.reason = "This field is required";
    if (!startDate?.isValid()) newErrors.startDate = "This field is required";
    if (!endDate?.isValid()) {
      newErrors.endDate = "This field is required";
    } else if (startDate?.isValid() && endDate.isBefore(startDate, "day")) {
      newErrors.endDate = "End date must be on or after the start date";
    }

    setErrors(newErrors);
    if (Object.keys(newErrors).length || !startDate || !endDate) return;

    onChange([
      ...leaves,
      {
        id: uuidv4(),
        reason: reason.trim(),
        startDate: formatDate(startDate),
        endDate: formatDate(endDate),
      },
    ]);
    setReason("");
    setStartDate(null);
    setEndDate(null);
  };

  return (
    <Box>
      <Typography className="dashboard-metric-label" sx={{ mb: 1 }}>
        Add Leave
      </Typography>
      <Grid
        container
        columnSpacing={2}
        rowSpacing={3}
        onKeyDown={(e) => e.key === "Enter" && e.preventDefault()}
      >
        <Grid size={12}>
          <TextField
            label="Reason"
            name="leaveReason"
            value={reason}
            error={toFieldError(errors.reason)}
            maxLength={100}
            required
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setReason(e.target.value)
            }
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <DatePicker
            label="Start Date"
            name="leaveStartDate"
            value={startDate}
            error={toFieldError(errors.startDate)}
            onChange={(value) => setStartDate(value)}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <DatePicker
            label="End Date"
            name="leaveEndDate"
            value={endDate}
            minDate={startDate ?? undefined}
            error={toFieldError(errors.endDate)}
            onChange={(value) => setEndDate(value)}
          />
        </Grid>
        <Grid size={12} sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Button
            label="Add Leave"
            variantType="primary"
            onClick={handleAddLeave}
            sx={{ px: 3 }}
          />
        </Grid>
      </Grid>

      <Typography className="dashboard-metric-label" sx={{ mt: 3, mb: 1 }}>
        Recorded Leave
      </Typography>
      <Box
        sx={{
          border: "1px solid var(--dashboard-border-divider)",
          borderRadius: "var(--dashboard-radius-control)",
          overflow: "hidden",
        }}
      >
        {sortedLeaves.map((leave) => (
          <Box
            key={leave.id}
            sx={{
              px: 1.5,
              py: 1,
              "&:not(:last-of-type)": {
                borderBottom: "1px solid var(--dashboard-border-row)",
              },
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography sx={{ fontSize: 14, fontWeight: 500 }}>
                {leave.reason || "—"}
              </Typography>
              {typeof leave.id === "string" && (
                <Typography
                  sx={{ fontSize: 11, color: theme.palette.warning.main }}
                >
                  Not saved yet
                </Typography>
              )}
            </Box>
            <Typography className="dashboard-helper-text">
              {formatDate(leave.startDate, DISPLAY_DATE_FORMAT)} –{" "}
              {formatDate(leave.endDate, DISPLAY_DATE_FORMAT)} ·{" "}
              {moment(leave.endDate).diff(moment(leave.startDate), "days") + 1}{" "}
              day(s)
            </Typography>
          </Box>
        ))}
        {sortedLeaves.length === 0 && (
          <Typography
            className="dashboard-helper-text"
            sx={{ px: 1.5, py: 1.5 }}
          >
            {fetchLeavesQuery.isLoading
              ? "Loading leave…"
              : fetchLeavesQuery.isError
                ? "Couldn't load leave"
                : "No leave recorded"}
          </Typography>
        )}
      </Box>
    </Box>
  );
};
